import { Editable, Slate, withReact } from "slate-react";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { StatusType, TaskType } from "App.types";
import { withHistory } from "slate-history";
import { createEditor, Transforms } from "slate";
import { ignoreTags } from "App.constants";
import { Button, Popconfirm } from "antd";
import { getCompletedSlate, withCustomLogic } from "App.helpers";
import { AudioOutlined, SoundFilled } from "@ant-design/icons";
import SentenceMaskedLeaf from "./SentenceLeaf";
import cx from "classnames";
import { SentenceTaskProps } from "./SentenceTask.type";
import { useAudioTranscript } from "./Helpers/useAudioTranscript";
import TaskPanel from "Components/TaskPanel";
import { useSpeechCheckRead } from "Hooks/useSpeechCheckRead";
import useSocketSpeech from "Hooks/useSocketSpeech";
import styles from "./SentenceTask.module.scss";

const initialValue = [{ children: [{ text: "" }] }];

const ReadTask: FC<SentenceTaskProps> = ({
  sentence,
  sentence: { text, tags, translate, id, storyId, button, info },
  onTaskComplete,
  onNext,
  children,
  audio,
  setDictOpened,
  alignCenter = false,
  transcripts,
  showGrammar,
  lesson,
  noTranslate,
  noText,
}) => {
  const [status, setStatus] = useState<StatusType>(StatusType.Editing);
  const [activeLeaf, setActiveLeaf] = useState<number | null>(null);

  const editor = useMemo(() => withReact(withHistory(withCustomLogic(createEditor()))), []);

  const {
    start,
    stop,
    results: speechResults,
  } = useSocketSpeech({
    setStatus,
    tags,
    text,
    interim_results: true,
  });

  useEffect(() => {
    audio?.addEventListener("play", stop);

    return () => audio?.removeEventListener("play", stop);
  }, [audio, stop]);

  const setInitialState = useCallback(() => {
    editor.children.forEach(() => {
      Transforms.delete(editor, { at: [0] });
    });

    editor.children = [];

    Transforms.insertNodes(editor, [{ children: getCompletedSlate(tags, text) }]);
  }, [tags, editor, text]);

  // initial
  useEffect(() => {
    setActiveLeaf(null);
    setStatus(StatusType.Editing);
    setInitialState();
  }, [sentence.id, editor, setInitialState]);

  // audio transcript
  useAudioTranscript({ setActiveLeaf, sentence, audio, transcripts });

  const { completedTagIdx, reset: resetSpeech } = useSpeechCheckRead({ speechResults, sentence });

  const renderLeaf = useCallback(
    (props: any) => (
      <SentenceMaskedLeaf
        active={status === StatusType.IsRecording && props.leaf.originalIdx >= completedTagIdx + 1}
        underlined={props.leaf.audioIdx === activeLeaf}
        wrong={status === "error" && props.leaf.idx === (activeLeaf ?? 0) + 1 && !ignoreTags.includes(props.leaf.text)}
        // underlined={status === "completed" || props.leaf.audioIdx <= (activeLeaf ?? -1) || props.leaf.audioIdx === (activeLeaf ?? -1)}
        showErrors={!["editing", "completed", "loading", "isRecording", ""].includes(status)}
        {...props}
      />
    ),
    [status, completedTagIdx, activeLeaf],
  );

  useEffect(() => {
    if (completedTagIdx >= tags.length - 1) {
      stop();
      setStatus(StatusType.Completed);
      onTaskComplete();
    }
  }, [completedTagIdx, onTaskComplete, stop, tags.length]);

  const onRecordClick = () => {
    if (status === StatusType.IsRecording) {
      setStatus(StatusType.Empty);
      stop();
    } else {
      resetSpeech();
      setStatus(StatusType.Loading);
      start();
    }
  };

  const onSkip = useCallback(() => {
    onTaskComplete().then(() => !info && onNext());
  }, [info, onNext, onTaskComplete]);

  return (
    <div className={styles.sentenceTask}>
      <div className={cx(styles.content, { [styles.content_hasChildren]: !!children, [styles.content_hasChildren]: !!children })}>
        <div className={styles.children}>{children}</div>

        <div className={cx(styles.slate, { [styles.slate_alignCenter]: alignCenter })}>
          {!noText && (
            <div className={styles.slate_wrapper}>
              <Slate editor={editor} initialValue={initialValue}>
                <Editable
                  className={styles.textArea}
                  readOnly
                  onKeyDown={() => setStatus(StatusType.Editing)}
                  renderLeaf={renderLeaf}
                  //renderElement={(props) => <StoryElement isActive={activeSent === props.element.id} play={play} {...props} />}
                />
              </Slate>
            </div>
          )}
          {(!noTranslate || status === StatusType.Completed) && <div className={styles.translate}>{translate}</div>}
        </div>

        <div className={styles.bottom}>
          <Button
            //style={{ visibility: status !== StatusType.Completed ? "hidden" : undefined }}
            icon={<SoundFilled />}
            onClick={() => audio?.play()}
          />
        </div>
      </div>

      <TaskPanel
        lessonId={lesson.id}
        task={TaskType.Read}
        sentId={id}
        storyId={storyId}
        showGrammar={showGrammar}
        onNext={onNext}
        isCompleted={status === StatusType.Completed}
        setDictOpened={setDictOpened}
        audio={audio}
        nextButton={button}
      >
        <span className={styles.buttons}>
          <Button
            onClick={onRecordClick}
            className={styles.record}
            type={"primary"}
            icon={<AudioOutlined />}
            shape="circle"
            data-recording={status === "isRecording"}
            loading={status === "loading"}
          ></Button>
          <Popconfirm title={"Не можешь говорить?"} onConfirm={onSkip}>
            <Button className={styles.btn_skip} size={"small"} type={"text"}>
              skip
            </Button>
          </Popconfirm>
        </span>
      </TaskPanel>
    </div>
  );
};

export default ReadTask;
